import type { Schemes } from "@ext/markdown/core/Parser/Parser";
import type MarkdownIt from "markdown-it/lib";
import type { RuleBlock } from "markdown-it/lib/parser_block";

const DELIMITER = /^\$\$[ \t]*$/;

/**
 * A display formula over several lines, `$$` alone on the lines around it — the form of GitHub, GitLab
 * and KaTeX. `MdParser` reads the one-line `$$ … $$` into a tag, but a tag attribute cannot hold a line break.
 */
const formulaBlock: RuleBlock = (state, startLine, endLine, silent) => {
	const lineText = (line: number) => state.src.slice(state.bMarks[line] + state.tShift[line], state.eMarks[line]);

	if (state.sCount[startLine] - state.blkIndent >= 4) return false;
	if (!DELIMITER.test(lineText(startLine))) return false;

	let closeLine = startLine + 1;
	for (; closeLine < endLine; closeLine++) {
		const text = lineText(closeLine);
		// LaTeX has no blank line in a formula, and a lone `$$` in prose must not take the article down to the next one.
		if (!text || state.sCount[closeLine] < state.blkIndent) return false;
		if (DELIMITER.test(text)) break;
	}
	if (closeLine >= endLine) return false;
	if (silent) return true;

	const body = state.getLines(startLine + 1, closeLine, state.sCount[startLine], false).replace(/\n$/, "");
	const content = `$$\n${body}\n$$`;
	const token = state.push("tag", "", 0);
	token.info = `formula content="${content}"`;
	token.meta = { tag: "formula", attributes: [{ type: "attribute", name: "content", value: content }] };
	token.map = [startLine, closeLine + 1];

	state.line = closeLine + 1;
	return true;
};

// A reader without the formula tag (chat answers, OpenAPI descriptions) would drop the formula: it keeps it as text.
const formulaBlockPlugin = (md: MarkdownIt, options?: { tags?: Schemes["tags"] }) => {
	if (!options?.tags?.formula) return;
	md.block.ruler.before("paragraph", "formulaBlock", formulaBlock, { alt: ["paragraph"] });
};

export default formulaBlockPlugin;
